/**
 * The pandit drawer's Money tab (doc 05-admin/07 §3.2 item 5): what we owe this pandit and what
 * has reached them, plus the financial-year ledger statement. Context only — every money action
 * lives on the payables and payouts screens (docs 16–17), so there is no button here that moves
 * money. The one write is the PAN check (doc 05-admin/19): a person's verdict on the card, which
 * is the only thing that moves a provider off the Section 206AA higher rate. It is typed, with an
 * audited reason, and a mismatch is recorded as readily as a match.
 *
 * **Built from the spec, not a design.** The design's pandit drawer has Quality, Badges and Jobs
 * only; the owner ruled on 2026-10-26 that design-less screens are built from the docs with the
 * console's own components and restyled later.
 *
 * Finance-gated: the drawer mounts this only for a finance (or superadmin) operator, so nobody
 * else's console asks the money endpoints a question it would be refused.
 */
import { useState } from 'react';
import { StatusChip } from '../components/primitives';
import { Input } from '../components/forms';
import { DataTable } from '../components/data';
import { EmptyState, ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, Money, ScopeAction, Section, StatTile } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import { sumPaise } from '../lib/money';
import { usePayables, type PayableRow } from '../lib/api/hooks/payables';
import { usePanditStatement } from '../lib/api/hooks/ledger';
import { usePanState, useVerifyPan, type PanState } from '../lib/api/hooks/tax';
import { currentFinancialYear } from './Tax';

/** One page is the summary: a pandit with more than this many payables is read on the queue. */
const PAGE_SIZE = 100;
const FY = /^\d{4}-\d{2}$/;

/** Owed and not yet in the bank: everything short of `settled`. */
export function moneySummary(rows: readonly PayableRow[]) {
  const open = rows.filter((r) => r.status !== 'settled');
  const settled = rows.filter((r) => r.status === 'settled');
  return {
    openCount: open.length,
    openPaise: sumPaise(open.map((r) => r.net_payable_paise)),
    heldCount: open.filter((r) => r.status === 'held' || r.status === 'on_hold_dispute').length,
    settledCount: settled.length,
    settledPaise: sumPaise(settled.map((r) => r.net_payable_paise)),
  };
}

export const PAN_STATES: Record<string, { label: string; tone: 'success' | 'warn' | 'error' | 'neutral' }> = {
  none: { label: 'No PAN on file', tone: 'neutral' },
  unverified: { label: 'Not checked — higher rate', tone: 'warn' },
  verified: { label: 'Verified', tone: 'success' },
  mismatch: { label: 'Mismatch — higher rate', tone: 'error' },
};

function PanCheck({ panditId }: { panditId: number }) {
  const { scope, toast } = useConsole();
  const state = usePanState(panditId);
  const verify = useVerifyPan();
  const [verdict, setVerdict] = useState<boolean | null>(null);

  if (state.isPending) return <LoadingState rows={1} message="Reading their PAN…" />;
  if (state.isError) return <ErrorState inline title="Could not read their PAN" cause={errorMessage(state.error)} />;
  const pan: PanState = state.data;
  const shown = PAN_STATES[pan.pan_status] ?? { label: pan.pan_status, tone: 'neutral' as const };

  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)', padding: 'var(--sp-md)' }}>
      <div style={{ display: 'flex', gap: 'var(--sp-sm)', alignItems: 'center', flexWrap: 'wrap' }}>
        <StatusChip tone={shown.tone} label={shown.label} />
        {pan.pan_last4 && <span style={{ fontFamily: 'var(--font-mono)' }}>PAN ending {pan.pan_last4}</span>}
        {pan.verified_at && (
          <span style={{ color: 'var(--c-ink2)' }}>
            last recorded {new Date(pan.verified_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}
          </span>
        )}
      </div>
      {pan.pan_status === 'none' ? (
        <span style={{ color: 'var(--c-ink2)' }}>They add their PAN themselves in the app; nobody can type it in for them.</span>
      ) : (
        <div style={{ display: 'flex', gap: 'var(--sp-sm)' }}>
          <ScopeAction scope={scope} need="finance" size="sm" disabled={verify.isPending} onClick={() => setVerdict(true)}>
            Card matches
          </ScopeAction>
          <ScopeAction scope={scope} need="finance" size="sm" variant="secondary" disabled={verify.isPending} onClick={() => setVerdict(false)}>
            Card does not match
          </ScopeAction>
        </div>
      )}
      {verify.isError && <ErrorState inline title="Not recorded" cause={errorMessage(verify.error)} />}
      {verdict !== null && (
        <ConfirmAction
          open
          title={verdict ? `Record that the card matches PAN ending ${pan.pan_last4}?` : `Record a mismatch on PAN ending ${pan.pan_last4}?`}
          phrase={verdict ? 'MATCH' : 'MISMATCH'}
          tone={verdict ? 'warn' : 'error'}
          consequences={
            verdict
              ? ['Their TDS drops from the Section 206AA higher rate to the standard rate on the next deduction.', 'A new row is written; the history keeps who said what, and when.']
              : ['They stay on the Section 206AA higher rate until a later check records a match.', 'A new row is written; the history keeps who said what, and when.']
          }
          reason="What you checked (audited)"
          confirmLabel={verdict ? 'Record match' : 'Record mismatch'}
          onCancel={() => setVerdict(null)}
          onConfirm={(reason) => {
            verify.mutate(
              { panditProfileId: panditId, matched: verdict, reason },
              { onSuccess: () => toast({ tone: 'success', message: verdict ? 'PAN recorded as verified' : 'PAN mismatch recorded' }) },
            );
            setVerdict(null);
          }}
        />
      )}
    </div>
  );
}

export function PanditMoney({ panditId }: { panditId: number }) {
  const [fy, setFy] = useState(currentFinancialYear());
  const payables = usePayables({ page: 1, page_size: PAGE_SIZE, pandit_profile_id: panditId });
  const validFy = FY.test(fy);

  return (
    <>
      <Section title="PAN" note="A person's check of the card — the only path off the higher TDS rate" pad={false}>
        <PanCheck panditId={panditId} />
      </Section>
      <Section title="Payables" note="Context only — holds and releases are on the payables queue">
        {payables.isPending && <LoadingState message="Reading their payables…" />}
        {payables.isError && <ErrorState title="Could not read their payables" cause={errorMessage(payables.error)} />}
        {payables.isSuccess && payables.data.items.length === 0 && (
          <EmptyState inline title="Nothing owed yet" body="No completed booking has accrued a payable for this pandit." />
        )}
        {payables.isSuccess && payables.data.items.length > 0 && (
          <PayableSummary rows={payables.data.items.map((item) => item.payable)} total={payables.data.total} />
        )}
      </Section>

      <Section title="Ledger statement" note="April to March, as their accountant reads it">
        <Input
          label="Financial year"
          value={fy}
          onChange={setFy}
          error={validFy ? undefined : 'A financial year reads 2026-27.'}
        />
        {validFy && <Statement panditId={panditId} fy={fy} />}
      </Section>
    </>
  );
}

function PayableSummary({ rows, total }: { rows: readonly PayableRow[]; total: number }) {
  const s = moneySummary(rows);
  return (
    <div style={{ display: 'grid', gap: 'var(--sp-md)' }}>
      {total > rows.length && (
        <StatusChip tone="warn" label={`Summing the latest ${rows.length} of ${total} payables — the queue has the rest`} />
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--sp-md)' }}>
        <StatTile label="Owed, not yet paid" value={<Money paise={s.openPaise} showPaise />} note={`${s.openCount} payables`} />
        <StatTile label="On hold" value={s.heldCount} note="held or in a dispute" tone={s.heldCount > 0 ? 'error' : 'default'} />
        <StatTile label="Settled" value={<Money paise={s.settledPaise} showPaise />} note={`${s.settledCount} reached the bank`} />
      </div>
      <DataTable
        density="compact"
        columns={[
          { key: 'booking', label: 'Booking', mono: true },
          { key: 'status', label: 'Status' },
          { key: 'net', label: 'Net payable', align: 'right', mono: true },
        ]}
        rows={rows.map((r) => ({
          id: r.id,
          booking: `#${r.booking_id}`,
          status: r.status.replace(/_/g, ' '),
          net: <Money paise={r.net_payable_paise} showPaise />,
        }))}
      />
    </div>
  );
}

function Statement({ panditId, fy }: { panditId: number; fy: string }) {
  const statement = usePanditStatement(panditId, fy);
  if (statement.isPending) return <LoadingState message="Reading the statement…" />;
  if (statement.isError) return <ErrorState title="Could not read the statement" cause={errorMessage(statement.error)} />;
  const st = statement.data;
  return (
    <div style={{ display: 'grid', gap: 'var(--sp-sm)', marginTop: 'var(--sp-sm)' }}>
      <div style={{ display: 'flex', gap: 'var(--sp-lg)', fontSize: 'var(--fs-admin-ui)' }}>
        <span>
          Opening <Money paise={st.opening_paise} showPaise />
        </span>
        <span>
          Closing <Money paise={st.closing_paise} showPaise bold />
        </span>
      </div>
      {st.lines.length === 0 ? (
        <EmptyState inline title="No movements" body={`Nothing was posted to this pandit's account in ${fy}.`} />
      ) : (
        <DataTable
          density="compact"
          columns={[
            { key: 'when', label: 'When' },
            { key: 'memo', label: 'Entry' },
            { key: 'amount', label: 'Amount', align: 'right', mono: true },
            { key: 'running', label: 'Balance', align: 'right', mono: true },
          ]}
          rows={st.lines.map((l, i) => ({
            id: `${l.transaction_id}-${i}`,
            when: l.occurred_at.slice(0, 10),
            memo: `${l.memo} · ${l.side}`,
            amount: <Money paise={l.amount_paise} showPaise />,
            running: <Money paise={l.running_paise} showPaise />,
          }))}
        />
      )}
    </div>
  );
}
